import { match } from "ts-pattern";

import {
  openedWhereRequired,
  progressOf,
  type GroupState,
} from "@thalamos/haven-common";

import type { ItemElement } from "../../components/AssessmentForm/answering.js";
import type { OpenedForm } from "./AssessmentContext.js";

/**
 * One item of a domain, and whether something is still due of it: an answer
 * the form requires, or what an answer given still lacks. An optional item
 * left unanswered has nothing due.
 */
export type DomainItem = { element: ItemElement; due: boolean };

/** A domain as its screens work through it: its title, any description, where it stands, and the items the form asks of it in the form's order. */
export type AssessmentDomain = {
  group: string;
  title: string;
  description: string | undefined;
  state: GroupState;
  items: readonly DomainItem[];
};

/**
 * Looking a domain up: found, not one the form asks given the answers so far,
 * or unknowable because no validator can be built from the schema.
 */
export type DomainLookup =
  | { kind: "found"; domain: AssessmentDomain }
  | { kind: "unknown" }
  | { kind: "unavailable" };

/**
 * The domain `group` of an opened form, as `progressOf` sees the draft: only
 * the items it asks count, and each says whether something is still due of it.
 */
export const domainOf = (opened: OpenedForm, group: string): DomainLookup => {
  const { schema, form, draft } = opened;

  return progressOf({
    schema,
    form,
    document: draft.document,
    context: draft.context,
    opened: openedWhereRequired(draft),
  }).match(
    (progress): DomainLookup => {
      const found = progress.groups.find((each) => each.group === group);
      const heading = form.find(
        (element) => element.kind === "group" && element.at.group === group,
      );

      if (found === undefined || heading === undefined) {
        return { kind: "unknown" };
      }

      const items = found.items.flatMap(
        ({ item, required, answered, done }): DomainItem[] => {
          const element = form.find(
            (each): each is ItemElement =>
              each.kind === "item" &&
              each.at.group === group &&
              each.at.item === item,
          );

          return element === undefined
            ? []
            : [{ element, due: !done && (required || answered) }];
        },
      );

      return {
        kind: "found",
        domain: {
          group,
          title: heading.presentation.title.text,
          description: heading.presentation.description?.text,
          state: found.state,
          items,
        },
      };
    },
    (): DomainLookup => ({ kind: "unavailable" }),
  );
};

/**
 * Where opening a domain lands: its first item while it is not opened, so a
 * reassessment's carried ratings are met from the start rather than skipped as
 * done; otherwise its first item with something `due`, in the form's order, or
 * its first item where none has. Undefined for a domain asking nothing.
 */
export const landingItemOf = (domain: AssessmentDomain): string | undefined =>
  match(domain.state)
    .with("not_opened", () => domain.items[0])
    .with(
      "not_started",
      "in_progress",
      "completed",
      () => domain.items.find((each) => each.due) ?? domain.items[0],
    )
    .exhaustive()?.element.at.item;
